import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, ArrowRight, FileCog, RefreshCw } from 'lucide-react';
import { StatusChip } from '../abf/components/StatusChip';
import { ViewportToast, type ActionFeedbackState } from '../abf/components/ActionFeedback';
import { Header, Note, SmallButton, Surface } from '../abf/onboarding/review/contractReviewStyles';
import {
  type BookingContract, type VersionView, draftFrom, getContracts, listVersions, startFromDefault, templateErrorMessage,
} from './documentTemplatesApi';
import { byType, documentWord, stateOf, versionPath, when } from './templateModel';

const DOCUMENT_PROCESSING = '/middle-office?tab=document-processing';

const Table = styled.div`
  border: 1px solid #29374b; border-radius: 10px; overflow: hidden; background: #0c1524;
  .row { display: grid; grid-template-columns: minmax(220px, 1.3fr) minmax(220px, 1.4fr) minmax(200px, 1.1fr) 230px;
    gap: 16px; align-items: center; padding: 15px 18px; }
  .row + .row { border-top: 1px solid #22303f; }
  .head { background: #0f1b2b; color: #91a5bd; font-size: 12px; font-weight: 650; letter-spacing: .04em; text-transform: uppercase; }
  .type strong { display: block; font-size: 14px; }
  .type small, .cell small { display: block; color: #a8b6ca; font-size: 12px; margin-top: 3px; overflow-wrap: anywhere; }
  .cell { display: flex; flex-direction: column; align-items: flex-start; gap: 5px; min-width: 0; }
  .cell a { color: #a6d8f5; text-decoration: none; font-size: 13px; }
  .action { display: flex; justify-content: flex-end; }
  .action button { border-color: #67c6f4; }
  details { margin-top: 4px; font-size: 12px; color: #a8b6ca; }
  details summary { cursor: pointer; }
  details li { margin: 4px 0; list-style: none; }
  details ul { padding: 0; margin: 6px 0 0; }
  details a { color: #a6d8f5; }
  @media (max-width: 1100px) {
    .row { grid-template-columns: 1fr 1fr; }
    .head { display: none; }
    .action { justify-content: flex-start; }
  }
  @media (max-width: 700px) { .row { grid-template-columns: 1fr; } }
`;

/**
 * Document templates, for the platform team only: what each document type is read into. One row per
 * type with the version in use, the version being prepared and the one next step.
 */
const DocumentTemplatesPage: React.FC = () => {
  const navigate = useNavigate();
  const [views, setViews] = useState<VersionView[]>([]);
  const [contracts, setContracts] = useState<Record<string, BookingContract>>({});
  const [loading, setLoading] = useState(true);
  const [busyType, setBusyType] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<ActionFeedbackState | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [versions, registered] = await Promise.all([listVersions(), getContracts()]);
      setViews(versions);
      setContracts(registered);
      setError(null);
    } catch (failure) {
      setError(templateErrorMessage(failure, 'The templates could not be loaded'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const rows = useMemo(() => {
    const grouped = byType(views);
    const types = Array.from(new Set([...Object.keys(contracts), ...Array.from(grouped.keys())])).sort();
    return types.map(type => ({ type, contract: contracts[type], ...(grouped.get(type) || { versions: [] }) }));
  }, [views, contracts]);

  const act = async (type: string, run: () => Promise<VersionView>) => {
    setBusyType(type);
    try {
      const created = await run();
      navigate(versionPath(created.template.id));
    } catch (failure) {
      setFeedback({ tone: 'error', message: templateErrorMessage(failure, 'The draft could not be started') });
      setBusyType(null);
    }
  };

  return (
    <Surface>
      <ViewportToast feedback={feedback} onClose={() => setFeedback(null)} />
      <Header>
        <Link className="back" to={DOCUMENT_PROCESSING}><ArrowLeft size={14} /> Document Processing</Link>
        <div className="heading">
          <div>
            <div className="eyebrow">Platform team</div>
            <h1>Document templates</h1>
            <div className="meta">
              <span>What each document type is read into. What a document books stays in code.</span>
            </div>
          </div>
          <div className="actions">
            <SmallButton type="button" onClick={() => void load()} disabled={loading}>
              <RefreshCw size={14} /> Refresh
            </SmallButton>
          </div>
        </div>
      </Header>

      <Note style={{ marginBottom: 18 }}>
        <p>
          A change is made on a draft, tried on a sample document, then proposed. It is used for new documents
          only after a second member of the team activates it. A type with no version in use is read with the
          built-in instructions.
        </p>
      </Note>

      {error && <Note role="alert" style={{ marginBottom: 18, borderColor: '#7f3b3b' }}>{error}</Note>}

      <Table role="table" aria-label="Document templates">
        <div className="row head" role="row">
          <span>Document</span><span>In use</span><span>Being prepared</span><span />
        </div>
        {loading && rows.length === 0 && <div className="row"><span>Loading…</span></div>}
        {rows.map(row => {
          const active = row.active?.template;
          const open = row.open?.template;
          const label = active?.label || open?.label || documentWord(row.type);
          const history = row.versions.filter(v => v !== row.active && v !== row.open);
          return (
            <div className="row" role="row" key={row.type}>
              <div className="type">
                <strong>{label}</strong>
                <small>{row.type}{row.contract ? '' : ' · no booking contract registered'}</small>
              </div>
              <div className="cell">
                {active ? (
                  <>
                    <Link to={versionPath(active.id)}>Version {active.version}</Link>
                    <small>Activated {when(active.activatedAt)} by {active.activatedBy}</small>
                  </>
                ) : (
                  <StatusChip tone="neutral" label="Built-in instructions" />
                )}
                {history.length > 0 && (
                  <details>
                    <summary>{history.length} earlier {history.length === 1 ? 'version' : 'versions'}</summary>
                    <ul>
                      {history.map(v => (
                        <li key={v.template.id}>
                          <Link to={versionPath(v.template.id)}>Version {v.template.version}</Link> · {stateOf(v.template.status).label}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
              <div className="cell">
                {open ? (
                  <>
                    <StatusChip tone={stateOf(open.status).tone} label={`Version ${open.version}: ${stateOf(open.status).label}`} />
                    <small>{row.open?.problems.length ? `${row.open.problems.length} problem(s) to fix` : `Started by ${open.createdBy}`}</small>
                  </>
                ) : <small>Nothing in progress</small>}
              </div>
              <div className="action">
                {open ? (
                  <SmallButton type="button" onClick={() => navigate(versionPath(open.id))}>
                    Continue version {open.version} <ArrowRight size={14} />
                  </SmallButton>
                ) : active ? (
                  <SmallButton type="button" disabled={busyType === row.type}
                    onClick={() => void act(row.type, () => draftFrom(active.id))}>
                    <FileCog size={14} /> Change it
                  </SmallButton>
                ) : row.contract ? (
                  <SmallButton type="button" disabled={busyType === row.type}
                    onClick={() => void act(row.type, () => startFromDefault(row.type))}>
                    <FileCog size={14} /> Start from the platform default
                  </SmallButton>
                ) : (
                  <small>Booking keys must be registered in code first</small>
                )}
              </div>
            </div>
          );
        })}
      </Table>
    </Surface>
  );
};

export default DocumentTemplatesPage;
